import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { placeDataSubmissionStatusToText } from "../../lib/place-data-submission-status-to-text";
import { placeStatusToText } from "../../lib/place-status-to-text";
import {
  approvePlaceDataSubmission,
  rejectPlaceDataSubmission,
} from "../../services/placeService";
import { ResolvedPlaceDataSubmission } from "../../types/Place";
import { PlaceDataSubmissionStatus } from "../../types/PlaceDataSubmissionStatus";
import { PlaceStatus } from "../../types/PlaceStatus";
import { VibeType } from "../../types/VibeType";
import { formatTimestamp } from "../../utils/format-timestamp";
import { Headings } from "../Headings";
import { PublicUserProfileLink } from "../PublicUserProfileLink/PublicUserProfileLink";
import DataComparison from "./DataComparison";

interface AdminSubmissionReviewCardProps {
  submission: ResolvedPlaceDataSubmission;
}

export default function AdminSubmissionReviewCard({
  submission,
}: AdminSubmissionReviewCardProps) {
  const [hideSubmission, setHideSubmission] = useState(false);

  const { submissionStatus, placeId } = submission;
  const defaultedSubmissionStatus =
    submissionStatus || PlaceDataSubmissionStatus.UNDER_REVIEW;
  const disableActions =
    submissionStatus === PlaceDataSubmissionStatus.ACCEPTED ||
    submissionStatus === PlaceDataSubmissionStatus.REJECTED;

  const handleAcceptSubmission = useCallback(async () => {
    if (!submission.id || !submission.placeId) {
      toast.error("Submission ID or Place ID is missing.");
      return;
    }

    const {
      id,
      placeId,
      place,
      creator,
      creatorId,
      createdAt,
      submissionStatus,
      ...submissionData
    } = submission;

    try {
      await approvePlaceDataSubmission(
        submission.id,
        submission.placeId,
        submissionData
      );
      setHideSubmission(true);
    } catch (err: any) {
      toast.error(err.message || "Failed to approve submission.");
    }
  }, [submission]);

  const handleRejectSubmission = useCallback(async () => {
    try {
      await rejectPlaceDataSubmission(submission.id);
      setHideSubmission(true);
    } catch (err: any) {
      toast.error(err.message || "Failed to reject submission.");
    }
  }, [submission]);

  const providedDetails = useMemo(() => {
    const { place, numberOfTables, vibes, status } = submission;

    const details = [];
    if (numberOfTables !== undefined) {
      details.push(
        <DataComparison<number>
          key="numberOfTables"
          title="Number of Tables"
          existingData={place?.numberOfTables}
          newData={numberOfTables}
          render={(v: number) => <span>{v}</span>}
        />
      );
    }
    if (vibes !== undefined) {
      details.push(
        <DataComparison<VibeType[]>
          key="vibes"
          title="Vibes"
          existingData={place?.vibes}
          newData={vibes}
          render={(v: VibeType[]) => <span>{v.join(", ")}</span>}
        />
      );
    }
    if (status !== undefined) {
      details.push(
        <DataComparison<PlaceStatus>
          key="status"
          title="Status"
          existingData={place?.status}
          newData={status}
          render={(s: PlaceStatus) => <span>{placeStatusToText(s)}</span>}
        />
      );
    }
    return details;
  }, [submission]);

  if (!submission.creator || hideSubmission) {
    return null;
  }

  return (
    <div className="submission-review-card admin">
      <Headings.PhoneSubBox>
        <Link to={`/place/${submission.place?.googlePlaceId}`}>
          {submission.place?.name}
        </Link>
        <span className={`status-badge ${defaultedSubmissionStatus}`}>
          {placeDataSubmissionStatusToText(defaultedSubmissionStatus)}
        </span>
      </Headings.PhoneSubBox>
      <div className="submission-metadata">
        <div className="submission-metadata-left">
          <PublicUserProfileLink
            profile={submission.creator}
            size="sm"
            linkToProfile
          />
          <div className="timestamp">
            {formatTimestamp(submission.createdAt)}
            &nbsp;&middot;&nbsp;
            <span>Place: {placeId}</span>
          </div>
        </div>
        <div className="submission-metadata-right"></div>
      </div>
      <div className="submission-details">{providedDetails}</div>
      <div className="submission-actions">
        <button
          className="btn btn-primary"
          onClick={handleAcceptSubmission}
          disabled={disableActions}
        >
          Accept Submission
        </button>
        <button
          className="btn btn-danger"
          onClick={handleRejectSubmission}
          disabled={disableActions}
        >
          Reject Submission
        </button>
      </div>
    </div>
  );
}
